import React, { useState, useCallback, useMemo } from 'react';
import { useNotifyTabLoading } from '../../../hooks/useTabLoadingReporter';
import { Inmate } from '@reliance/types/models/Inmate';
import { InmateSupportAssignedToType } from '@reliance/types/enums/InmateSupportAssignedToType';
import api from '../../../common/api';
import SupportMessageSend from './SupportMessageSend';
import MarkAllReadButton from './MarkAllReadButton';
import SupportMessagesTable from './SupportMessagesTable';
import { useAlerts } from '../../../alert/AlertContext';

type SupportTabProps = {
  inmate?: Inmate;
  tabSelected?: boolean;
  onInmateReload?: () => void;
  externalRefreshTrigger?: number;
};

const SupportTab: React.FC<SupportTabProps> = (props) => {
  const { inmate, tabSelected, onInmateReload, externalRefreshTrigger } = props;
  const alerts = useAlerts();

  const [supportMessageReading, setSupportMessageReading] = useState(false);
  const [internalRefreshTrigger, setInternalRefreshTrigger] = useState(0);
  const [supportMessagesLoading, setSupportMessagesLoading] = useState(false);

  useNotifyTabLoading('support-messages', supportMessagesLoading || supportMessageReading);

  // Use external refresh trigger if provided, otherwise use internal
  // Combine both to ensure refreshes from either source work
  const refreshTrigger = useMemo(() => {
    if (externalRefreshTrigger !== undefined) {
      return Math.max(externalRefreshTrigger, internalRefreshTrigger);
    }
    return internalRefreshTrigger;
  }, [externalRefreshTrigger, internalRefreshTrigger]);

  const refresh = useCallback(() => {
    setInternalRefreshTrigger(prev => prev + 1);
  }, []);

  const handleMessageSent = useCallback(() => {
    if (onInmateReload) {
      onInmateReload();
    }
    refresh();
  }, [onInmateReload, refresh]);

  const handleRead = useCallback(async () => {
    if (!inmate) {
      return;
    }

    setSupportMessageReading(true);
    try {
      await api.put('/text-message/process-unread-support-requests', { inmateId: inmate.id });

      alerts.success('Messages successfully marked as read');
      refresh();
    } catch (error) {
      alerts.error('An error occurred while marking the messages as read');
    } finally {
      setSupportMessageReading(false);
    }
  }, [inmate, refresh]);

  const handleReassign = useCallback(async (assignedToType: InmateSupportAssignedToType) => {
    if (!inmate) {
      return;
    }

    try {
      await api.put(`/inmate-support/${inmate.id}`, { assignedToType });

      alerts.success('Support assignment updated successfully');

      if (onInmateReload) {
        onInmateReload();
      }

      refresh();
    } catch (error) {
      alerts.error('An error occurred while updating the support assignment');
      throw error; // Re-throw so SupportAssignmentInfo can handle it
    }
  }, [inmate, onInmateReload, refresh]);

  const handleRefund = useCallback(async (activity: { activityId: number; notifyInmate: boolean; description: string }) => {
    try {
      await api.post('/inmate-account/activity/refund', activity);

      alerts.success('The refund was successfully applied');
      refresh();
    } catch (error) {
      alerts.error('An error occurred while applying refund');
      throw error; // Re-throw so SupportMessagesTable can handle it
    }
  }, [refresh]);

  const handleTableLoadingChange = useCallback((loading: boolean) => {
    setSupportMessagesLoading(loading);
  }, []);

  if (!inmate) {
    return null;
  }

  return (
    <div>
      {inmate.device && (
        <SupportMessageSend
          inmate={inmate}
          onMessageSent={handleMessageSent}
        />
      )}
      <MarkAllReadButton
        inmate={inmate}
        reading={supportMessageReading}
        onRead={handleRead}
        loading={supportMessagesLoading}
      />
      <SupportMessagesTable
        inmateId={inmate.id}
        onRefund={handleRefund}
        tabSelected={tabSelected}
        onLoadingChange={handleTableLoadingChange}
        refreshTrigger={refreshTrigger}
        onReassign={handleReassign}
      />
    </div>
  );
};

export default SupportTab;
